import React, { Suspense, useEffect, useMemo } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import EncapsulationRoutes from '../modules/encapsulation/Routes';
import RegisterRoutes from '../modules/account/configs/RoutesConfig';
import BaseRoutes from '../components/pages/Routes';
import { useModulesContext } from '../services/providers/ModulesProvider';
import DynamicPage from 'components/pages/DynamicPage';
import { ApiLocalStorage } from './ApiLocalStorage';
import { useAuth } from 'auth/AuthProvider';
import { RouteEnum } from 'modules/account/constantes/RouteEnum';
import { startSessionWatcher } from 'modules/account/pages/authentication/Logout';
import { USER } from '../modules/account/constantes/LocalStorageEnum';
import { useBackgroundByRoute } from '../hooks/useBackgroundByRoute';

interface RouteConfig {
  path: string;
  element: React.ReactNode;
}

const AppRoutes: React.FC = () => {
  const modules = useModulesContext();
  const location = useLocation();
  const navigate = useNavigate();
  const {logout} = useAuth();
  
  // Aplicar background específico por rota
  useBackgroundByRoute();
  
  const pageModules = modules.filter(
    (module) => module.componentPosition === 'page' && module.componentPage
  );
  
  useEffect(() => {
    const stopWatcher = startSessionWatcher(logout, navigate, location.pathname);
    
    return stopWatcher;
  }, [logout, navigate]);
  
  const allRoutes = useMemo(() => {
    const routes: RouteConfig[] = [
      ...Object.values(EncapsulationRoutes),
      ...Object.values(RegisterRoutes),
      ...Object.values(BaseRoutes)
    ];
    
    return routes;
  }, []);
  
  const ensureLeadingSlash = (componentPage: string): string => {
    if (!componentPage.startsWith('/')) {
      return `/${componentPage}`;
    }
    
    return componentPage;
  }
  
  useEffect(() => {
    let tries = 0;
    
    const urlsLogin = [
      {
        path: RouteEnum.WELCOME,
        user: true
      },
      {
        path: RouteEnum.LOGIN_USERNAME,
        user: true
      },
      {
        path: RouteEnum.LOGIN_PASSWORD,
        user: true
      },
      {
        path: RouteEnum.OTP_REQUEST,
        user: true
      }
    ];
    
    const checkUser = () => {
      const currentPath = location.pathname;
      const user = ApiLocalStorage.get(USER);
      
      if (user || tries >= 3) {
        const isPageModuleRoute = pageModules.some(module => ensureLeadingSlash(module.componentPage) === ensureLeadingSlash(currentPath));
        const isAllRoute = urlsLogin.some(route => ensureLeadingSlash(route.path) === ensureLeadingSlash(currentPath));
        
        if (isPageModuleRoute && !user) {
          if (currentPath !== RouteEnum.LOGIN_USERNAME) {
            navigate(RouteEnum.LOGIN_USERNAME);
          }
        } else if (isAllRoute && user) {
          const enumToArray = (enumObject: any): string[] => Object.keys(enumObject).map(key => enumObject[key]);
          
          const routeArray: string[] = enumToArray(RouteEnum);
          const isAuthRoute = routeArray.includes(currentPath);
          
          if (isAuthRoute && currentPath !== '/home') {
            navigate('/home');
          }
        }
      } else {
        tries++;
        setTimeout(checkUser, 300);
      }
    };
    
    const timeoutAuth = setTimeout(checkUser, 500);
    
    return () => clearTimeout(timeoutAuth);
  }, [location.pathname, pageModules, allRoutes, navigate]);
  
  return (
    <Routes>
      {pageModules.map((module) => (
        <Route
          key={module.id}
          path={module.componentPage}
          element={
            <Suspense fallback={<div>Carregando...</div>}>
              <DynamicPage/>
            </Suspense>
          }
        />
      ))}
      
      {allRoutes.map((route) => (
        <Route key={route.path} path={route.path} element={route.element}/>
      ))}
    
    </Routes>
  );
};

export default AppRoutes;
